/**
 * CORE-740 — where a scheduled live's announcement actually lands.
 *
 * The backend publishes the master's feed announcement (a `MediaAnnouncement`
 * MasterPost) only when the live starts at least two hours out. Sooner than
 * that, `CommunityFeedService.CreateAnnouncementPostAsync` silently returns
 * `null` (the CORE-325 lead-time gate: `eventStartUtc < UtcNow + 2h`) and the
 * session is listed only in the master's «События» / calendar.
 *
 * `LiveStreamModal` (the legacy Dashboard create surface) used to promise the
 * feed post unconditionally — its «Сообщение в вашу ленту» hint and the
 * success modal both said the anons «появится … в вашей ленте», so a master
 * who scheduled a session <2h out (QA's 23.08 scenario, 14 min ahead) was told
 * a feed card would appear when the gate guaranteed none would. This mirrors
 * the gate on the client so the copy stays honest, matching what the unified
 * wizard (WizardSteps) already tells the master.
 *
 * Pure (no React / RN imports) so the contract unit-tests without a DOM.
 */

/** Mirror of `CommunityFeedService.FeedAnnouncementMinLeadTime` (2 hours). */
export const FEED_ANNOUNCEMENT_MIN_LEAD_MS = 2 * 60 * 60 * 1000;

/**
 * True when the backend will suppress the feed announcement for a live
 * starting at `startUtcISO`. Matches the BE's strict `start < now + lead`
 * comparison. Fails open (returns false → keep the feed-promise copy) when the
 * start time can't be parsed, so a bad input never hides a post that may exist.
 */
export function feedAnnouncementSuppressed(startUtcISO: string, nowMs: number): boolean {
    const start = new Date(startUtcISO).getTime();
    if (Number.isNaN(start)) return false;
    return start - nowMs < FEED_ANNOUNCEMENT_MIN_LEAD_MS;
}

/**
 * Success-modal body. The with-guests preview hint is identical on both
 * branches; only the placement sentence changes so the master isn't promised a
 * feed card the 2-hour gate will withhold.
 */
export function announceSuccessBody(
    title: string,
    opts: { suppressed: boolean; cohosts: boolean; programOnly?: boolean },
): string {
    // CORE-879 — Live-сессию из платной программы сервер никуда публично не
    // выносит: ни анонса в ленте, ни места в общем календаре. Обещать их
    // мастеру — значит ввести его в заблуждение о том, кто её увидит.
    const placement = opts.programOnly
        ? `«${title}» видна только тем, кто купил программу: она в их календаре и в составе программы. В ленту и в общий календарь она не попадает.`
        : opts.suppressed
            ? `«${title}» появится в событиях мастера и в календаре. До старта меньше двух часов, поэтому анонс в ленте на этот раз не публикуется — запланируйте сессию заранее, чтобы карточка вышла в ленту.`
            : `«${title}» появится в общей и в вашей ленте, а также в календаре.`;
    const tail = opts.cohosts
        ? ' Когда будете готовы, нажмите «Предпросмотр Live-сессии» на странице события — внутри Live-сессии есть кнопка «Пригласить гостя»: отправьте ссылку другу или собеседнику для интервью, проверьте звук и картинку до старта.'
        : ' Когда будете готовы, нажмите «Предпросмотр Live-сессии» на странице события — проверьте звук и картинку до старта.';
    return placement + tail;
}

/** Sublabel under the «Сообщение в вашу ленту» composer. */
export function feedMessageHint(suppressed: boolean, programOnly = false): string {
    // CORE-879 — у Live-сессии программы анонса в ленте нет вовсе (см. выше).
    if (programOnly) return 'Live-сессия входит в программу — анонс в ленту не публикуется.';
    return suppressed
        ? 'До старта меньше двух часов — анонс в ленту не публикуется, сессия останется в ваших событиях. Сообщение сохранится в описании события.'
        : 'Это сообщение появится вместе с анонсом в вашей ленте — добавьте личное приглашение.';
}
